import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, BugIcon, CheckCircle2, XCircle, ChevronRight, ExternalLink } from 'lucide-react';
interface Props {
  fallbackTitle?: string;
  projectKey?: string;
  children: ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isExpanded: boolean;
  bugReportState: 'idle' | 'confirming' | 'creating' | 'success' | 'error';
  bugReportError: string | null;
  createdIssueKey: string | null;
  priority: string;
  projectKey: string;
}
export class SectionErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    isExpanded: false,
    bugReportState: 'idle',
    bugReportError: null,
    createdIssueKey: null,
    priority: 'High',
    projectKey: ''
  };
  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorInfo: null,
      isExpanded: false,
      bugReportState: 'idle'
    };
  }
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in section:', error, errorInfo);
    let defaultProject = 'PIAE';
    try {
      const brandConfig = JSON.parse(localStorage.getItem('vsm_brand_config') || '{}');
      if (brandConfig.projectKey) {
        defaultProject = brandConfig.projectKey;
      }
    } catch (e) {}
    this.setState({
      errorInfo,
      projectKey: this.props.projectKey || defaultProject
    });
  }
  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isExpanded: false,
      bugReportState: 'idle',
      bugReportError: null,
      createdIssueKey: null
    });
  };
  private handleCreateBug = async () => {
    this.setState({
      bugReportState: 'creating',
      bugReportError: null
    });
    try {
      // Dynamic import to avoid circular dependencies or issues in class component
      const {
        runTool
      } = await import('../../services/toolRunner');
      const userEmail = localStorage.getItem('vsm_active_user') || 'Usuario Desconocido';
      const sectionName = this.props.fallbackTitle || 'Sección Desconocida';
      const errorMsg = this.state.error?.message || 'Error desconocido';
      const stack = this.state.errorInfo?.componentStack || 'Sin stack trace';
      const summary = `[Bug Frontend] Error en ${sectionName}: ${errorMsg.substring(0, 80)}`;
      const description = `
*Reporte Automático de Error Frontend*

*Detalles del Entorno:*
* *Usuario:* ${userEmail}
* *Fecha:* ${new Date().toLocaleString('es-AR')}
* *Sección:* ${sectionName}
* *URL:* ${window.location.href}
* *Navegador:* ${navigator.userAgent}

*Mensaje de Error:*
{code}
${this.state.error?.toString()}
{code}

*Component Stack:*
{code}
${stack}
{code}
      `.trim();
      const result = await runTool('create-bug', {
        project: this.state.projectKey,
        summary,
        description,
        priority: this.state.priority
      });
      if (result.success && result.data?.key) {
        this.setState({
          bugReportState: 'success',
          createdIssueKey: result.data.key
        });
      } else {
        this.setState({
          bugReportState: 'error',
          bugReportError: result.error || 'Error desconocido al crear el bug en Jira'
        });
      }
    } catch (err: any) {
      this.setState({
        bugReportState: 'error',
        bugReportError: err.message || 'Error al ejecutar la herramienta'
      });
    }
  };
  public render() {
    if (this.state.hasError) {
      const {
        bugReportState,
        bugReportError,
        createdIssueKey,
        priority,
        projectKey
      } = this.state;
      const userEmail = localStorage.getItem('vsm_active_user') || 'Desconocido';
      const sectionName = this.props.fallbackTitle || 'Desconocido';
      return <div className="flex flex-col items-center justify-center p-8 h-full min-h-[300px] bg-slate-900/50 rounded-xl border border-red-900/30 overflow-y-auto" data-id="element-2555">
          <div className="w-12 h-12 rounded-xl bg-red-950/50 border border-red-900/50 flex items-center justify-center mb-4 shrink-0" data-id="element-2556">
            <AlertTriangle className="text-red-400" size={24} data-id="element-2557" />
          </div>
          <h3 className="text-lg font-bold text-slate-100 mb-2 text-center" data-id="element-2558">
            {this.props.fallbackTitle || 'Error al cargar esta sección'}
          </h3>
          <p className="text-sm text-slate-400 text-center max-w-md mb-6" data-id="element-2559">
            Ha ocurrido un problema inesperado. Podés reintentar cargar la
            sección, revisar los detalles o reportar el bug directamente a Jira.
          </p>

          {/* Action Buttons */}
          {bugReportState === 'idle' && <div className="flex flex-wrap justify-center gap-3 mb-6" data-id="element-2560">
              <button onClick={this.handleReset} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2561">
                <RefreshCw size={16} data-id="element-2562" />
                Reintentar
              </button>
              <button onClick={() => this.setState({
            bugReportState: 'confirming'
          })} className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2563">
                <BugIcon size={16} data-id="element-2564" />
                Reportar Bug
              </button>
              <button onClick={() => this.setState(s => ({
            isExpanded: !s.isExpanded
          }))} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2565">
                {this.state.isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
              </button>
            </div>}

          {/* Bug Report Confirmation Panel */}
          {bugReportState === 'confirming' && <div className="w-full max-w-2xl bg-slate-950 border border-amber-900/50 rounded-xl p-5 mb-6 text-left animate-in fade-in slide-in-from-top-4" data-id="element-2566">
              <h4 className="text-base font-bold text-slate-100 mb-4 flex items-center gap-2" data-id="element-2567">
                <BugIcon size={18} className="text-amber-500" data-id="element-2568" />
                Reportar Bug en Jira
              </h4>

              <div className="space-y-4" data-id="element-2569">
                <div data-id="element-2570">
                  <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-2571">
                    Proyecto Jira
                  </label>
                  <input type="text" value={projectKey} onChange={e => this.setState({
                projectKey: e.target.value.toUpperCase()
              })} className="w-full max-w-[150px] bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-500" placeholder="Ej: PIAE" data-id="element-2572" />
                </div>

                <div data-id="element-2573">
                  <label className="block text-xs font-medium text-slate-400 mb-1" data-id="element-2574">
                    Prioridad
                  </label>
                  <div className="flex gap-2" data-id="element-2575">
                    {['High', 'Medium', 'Low'].map(p => <button key={p} onClick={() => this.setState({
                  priority: p
                })} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${priority === p ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:bg-slate-800'}`} data-id="element-2576">
                        {p}
                      </button>)}
                  </div>
                </div>

                <div className="bg-slate-900 rounded-lg p-3 border border-slate-800" data-id="element-2577">
                  <p className="text-xs text-slate-500 mb-2 font-medium uppercase tracking-wider" data-id="element-2578">
                    Datos que se enviarán automáticamente:
                  </p>
                  <ul className="text-sm text-slate-300 space-y-1.5" data-id="element-2579">
                    <li data-id="element-2580">
                      <span className="text-slate-500" data-id="element-2581">Usuario:</span>{' '}
                      {userEmail}
                    </li>
                    <li data-id="element-2582">
                      <span className="text-slate-500" data-id="element-2583">Módulo:</span>{' '}
                      {sectionName}
                    </li>
                    <li data-id="element-2584">
                      <span className="text-slate-500" data-id="element-2585">Error:</span>{' '}
                      <span className="font-mono text-xs text-red-400" data-id="element-2586">
                        {this.state.error?.message}
                      </span>
                    </li>
                    <li data-id="element-2587">
                      <span className="text-slate-500" data-id="element-2588">Stack trace:</span>{' '}
                      Incluido
                    </li>
                    <li data-id="element-2589">
                      <span className="text-slate-500" data-id="element-2590">URL & Browser:</span>{' '}
                      Incluidos
                    </li>
                  </ul>
                </div>

                <div className="flex items-center gap-3 pt-2" data-id="element-2591">
                  <button onClick={this.handleCreateBug} disabled={!projectKey} className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-2592">
                    Crear Bug en Jira
                  </button>
                  <button onClick={() => this.setState({
                bugReportState: 'idle'
              })} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2593">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>}

          {/* Creating State */}
          {bugReportState === 'creating' && <div className="w-full max-w-md bg-slate-950 border border-slate-800 rounded-xl p-6 mb-6 flex flex-col items-center justify-center text-center animate-in fade-in" data-id="element-2594">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" data-id="element-2595" />
              <p className="text-sm font-medium text-slate-200" data-id="element-2596">
                Creando bug en Jira...
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-2597">
                Enviando detalles del error y stack trace
              </p>
            </div>}

          {/* Success State */}
          {bugReportState === 'success' && <div className="w-full max-w-md bg-emerald-950/30 border border-emerald-900/50 rounded-xl p-6 mb-6 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95" data-id="element-2598">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-3" data-id="element-2599">
                <CheckCircle2 className="text-emerald-400" size={24} data-id="element-2600" />
              </div>
              <h4 className="text-base font-bold text-emerald-400 mb-1" data-id="element-2601">
                Bug Reportado Exitosamente
              </h4>
              <p className="text-sm text-slate-300 mb-4" data-id="element-2602">
                Se ha creado el ticket{' '}
                <strong className="text-slate-100" data-id="element-2603">{createdIssueKey}</strong> en
                Jira.
              </p>
              <div className="flex gap-3" data-id="element-2604">
                <button onClick={this.handleReset} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2605">
                  Volver a intentar cargar
                </button>
              </div>
            </div>}

          {/* Error State */}
          {bugReportState === 'error' && <div className="w-full max-w-2xl bg-red-950/30 border border-red-900/50 rounded-xl p-5 mb-6 text-left animate-in fade-in" data-id="element-2606">
              <div className="flex items-start gap-3" data-id="element-2607">
                <XCircle className="text-red-400 shrink-0 mt-0.5" size={20} data-id="element-2608" />
                <div data-id="element-2609">
                  <h4 className="text-sm font-bold text-red-400 mb-1" data-id="element-2610">
                    Error al crear el bug
                  </h4>
                  <p className="text-sm text-slate-300 mb-4" data-id="element-2611">
                    {bugReportError}
                  </p>
                  <div className="flex gap-3" data-id="element-2612">
                    <button onClick={this.handleCreateBug} className="px-4 py-2 bg-red-900/50 hover:bg-red-800/50 text-red-200 text-sm font-medium rounded-lg transition-colors border border-red-800/50" data-id="element-2613">
                      Reintentar creación
                    </button>
                    <button onClick={() => this.setState({
                  bugReportState: 'idle'
                })} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-medium rounded-lg transition-colors" data-id="element-2614">
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>}

          {/* Error Details (Collapsible) */}
          {this.state.isExpanded && this.state.error && bugReportState === 'idle' && <div className="w-full max-w-2xl bg-slate-950 border border-slate-800 rounded-lg p-4 overflow-auto text-left animate-in fade-in slide-in-from-top-2" data-id="element-2615">
                <p className="text-xs font-mono text-red-400 mb-2 whitespace-pre-wrap" data-id="element-2616">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && <p className="text-[10px] font-mono text-slate-500 whitespace-pre-wrap" data-id="element-2617">
                    {this.state.errorInfo.componentStack}
                  </p>}
              </div>}
        </div>;
    }
    return this.props.children;
  }
}